/**
 * Questions — what a Customer is asked, on the form and on the phone.
 *
 * A settings section of its own, beside Phone rather than under it. It
 * was one row in an Intake section that also held the number's config
 * and the notification email, which put the one list a Company actually
 * edits at the same level as two screens of plumbing (ADR-0022).
 *
 * One list per form, asked on the public form and by the Receptionist
 * alike (ADR-0022). The screen is a list of sentences — "Will there be a
 * live band?" — and nothing else: no kinds, no choices, no phone
 * wording. The Company writes the questions; the server writes the form
 * for them, keeping every block whose sentence did not change and
 * writing one for each sentence that is new or reworded (#80).
 *
 * The one shape a Company does decide is when a sentence is asked: a
 * question may wait on the answer to any yes-or-no or pick-one question
 * above it, or on whether the event is indoors or outdoors, and on
 * several of those at once (ADR-0032). Each condition is a quiet pair of
 * selects under the sentence — which question, which answer — and the
 * questions offered are the ones the server already wrote as one of
 * those two kinds, because the answer it waits on is one the form
 * offers. It was a tree of sub-questions one level deep, which could
 * not say "ask this if X and Y".
 *
 * Every settings control commits itself (DESIGN.md, Save model), and so
 * does a sentence: leaving the line sends the list. The generate step
 * behind it runs where the Company cannot see it — nothing locks, nothing
 * spins, and a list that could not be written says so under the rows.
 * It had an Update button and a read-only "On the form" block for one
 * afternoon; the button was a thing to forget to press, and the block
 * was a form nobody asked to see.
 *
 * Which question means dry hire is the generate step's call, not the
 * Company's: one question at most, never one waiting on another, and
 * the answer that means it is shown under that question as a quiet line
 * with nothing to press. It was not drawn at all until a sub-question
 * was marked in production and nobody at the Company could see why a
 * job drafted with no crew or transport.
 *
 * The form's event types (#197) are not edited here. A Company's list
 * is set up with the form and changed in the database, and the screen
 * says only that the event type is always asked. It was a list of
 * labels above each form's questions from #197 until it came off.
 *
 * It was an in-place editor of every attribute of every row once — a
 * kind select, a choices cell, a phone cell, an "if yes" row — and the
 * first reader could not use it. A list of sentences is what a Company
 * has in its head when it thinks about its form.
 */
import { useState } from "react";
import { useSearchParams } from "react-router";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { Select } from "../../components/ui/Select";
import { ToggleSettingRow } from "../../components/ui/SettingRow";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from "../../components/ui/icons";
import {
  getInquiryQuestions,
  saveInquiryPickUp,
  saveInquirySiteVisit,
  saveInquiryProfileLabel,
  saveInquiryQuestions,
  type InquiryQuestionForm,
} from "../../lib/settings-api";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { blurOnEnter } from "../../lib/use-action";
import { useSettingsSource } from "../../lib/use-settings-source";
import { EstimateSettings } from "./EstimateSettings";
import * as list from "./question-list";
import {
  answersFor,
  dryHireOf,
  lineCount,
  MAX_CONDITIONS,
  MAX_QUESTIONS,
  sourcesOf,
  withCurrent,
} from "./question-list";
import { useQuestionListSave } from "./useQuestionListSave";
import {
  BackLink,
  DrillDownRow,
  DrillList,
  Loading,
  SectionHeader,
  SettingsGroup,
} from "./section-chrome";
import styles from "./InquiryQuestions.module.css";

export { MAX_QUESTIONS };

/** The one page Questions opens, as it reads in `?page=`. */
const ESTIMATE_PAGE = "estimate";

export function InquiryQuestions() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read again on the way back from the Estimate: switching it off is
  // what frees the questions it prices from, and the lists are drawn
  // afresh rather than from what they held before.
  const onEstimatePage = searchParams.get("page") === ESTIMATE_PAGE;
  const source = useSettingsSource(getInquiryQuestions, {
    skip: onEstimatePage,
  });
  const { loadError } = source;
  const forms = source.data?.forms ?? null;
  const alwaysAsks = source.data?.always_asks ?? "";
  const quotingOff = source.data?.quoting_off ?? false;

  /** `?page=` is set and cleared in place: a drill is not history. */
  const showPage = (page: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (page === null) next.delete("page");
    else next.set("page", page);
    setSearchParams(next, { replace: true });
  };


  if (onEstimatePage) {
    return (
      <div>
        <BackLink label="Questions" onClick={() => showPage(null)} />
        <EstimateSettings />
      </div>
    );
  }

  return (
    <section>
      <SectionHeader
        title="Questions"
        description={alwaysAsks}
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      <div className={styles.forms}>
        {!forms ? (
          <Loading />
        ) : (
          forms.map((form) => (
            <QuestionsGroup key={form.profile_key} initial={form} />
          ))
        )}

        {/* What the answers add up to, one step on. It was a section of its
            own in the sidebar, which put a spread and a floor — set once and
            left — beside the four screens a Company comes back to. */}
        {/* A Company that does not quote has no range to show (#273), and a
            switch that changes nothing reads as a broken one. */}
        {forms && !quotingOff && (
          <DrillList>
            <DrillDownRow
              name="Estimate"
              description="The price range a customer sees before the quote."
              onOpen={() => showPage(ESTIMATE_PAGE)}
            />
          </DrillList>
        )}
      </div>
    </section>
  );
}

/** One form's list. */
function QuestionsGroup({ initial }: { initial: InquiryQuestionForm }) {
  const { form, draft, setDraft, error, commit } = useQuestionListSave(
    initial,
    (sentences) => saveInquiryQuestions(initial.profile_key, sentences),
  );
  // A Company does not have "a Profile"; it has forms it calls
  // something, and the heading is the name. profile_key stays hidden:
  // it is what the form's template and Role mapping hang off, so it
  // cannot follow a Company's naming.
  const name = useSettingCommit(initial.label, (next) =>
    saveInquiryProfileLabel(initial.profile_key, next).then(() => undefined),
  );

  // Whether a Customer may collect the gear is the form's, beside the
  // list whose reading it changes (migration 052): off, the call never
  // asks, and a question marked for a pick-up is a question like any
  // other.
  const [pickUp, setPickUp] = useState(initial.pick_up);
  // Whether the form and the call offer a site visit (migration 053).
  const [siteVisit, setSiteVisit] = useState(initial.site_visit);

  const count = lineCount(draft);
  const full = count >= MAX_QUESTIONS;
  const dryHire = pickUp ? dryHireOf(form.questions) : null;

  // Typing and adding a line wait for the line to be left; everything
  // else — a move, a removal, a condition added, picked or removed, a
  // line left — is a commit of its own, because there is no line to
  // leave.
  const changeAndCommit = (next: typeof draft) => {
    setDraft(next);
    void commit();
  };

  const add = () => setDraft(list.add(draft));

  return (
    <SettingsGroup
      id={`inquiry-questions-${form.profile_key}`}
      titleText={name.draft}
      title={
        <input
          aria-label="Form name"
          className={styles.formName}
          value={name.draft}
          disabled={name.saving}
          onChange={(e) => name.setDraft(e.target.value)}
          onFocus={name.onFocus}
          onBlur={() => void name.commitDraft()}
          onKeyDown={blurOnEnter}
        />
      }
    >
      {name.error && (
        <Banner variant="error" className={styles.notice}>
          {name.error}
        </Banner>
      )}

      <div className={styles.switches}>
        <ToggleSettingRow
          id={`inquiry-pick-up-${form.profile_key}`}
          title="Dry hire"
          description="The customer can collect the gear and run it themselves."
          checked={pickUp}
          onChange={(next) =>
            saveInquiryPickUp(initial.profile_key, next).then(setPickUp)
          }
        />
        <ToggleSettingRow
          id={`inquiry-site-visit-${form.profile_key}`}
          title="Site visit"
          description="Offered to every customer before you quote."
          checked={siteVisit}
          onChange={(next) =>
            saveInquirySiteVisit(initial.profile_key, next).then(setSiteVisit)
          }
        />
      </div>

      <ListHeader
        title="Questions"
        count={`${count} of ${MAX_QUESTIONS}`}
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={add}
            disabled={full}
          >
            Add a question
          </Button>
        }
      />
      <ol className={styles.sentences}>
        {draft.map((sentence, index) => {
          const n = index + 1;
          const sources = sourcesOf(draft, index, form.questions);
          const when = sentence.when ?? [];
          // A new condition waits on the first thing offered and its
          // first answer, and the pair is written at once: a condition
          // half picked is a condition the form cannot read.
          const addCondition = () => {
            const field = sources[0].value;
            const equals = answersFor(field, form.questions)[0]?.value ?? "";
            changeAndCommit(list.addCondition(draft, index, field, equals));
          };
          return (
            <SentenceRow
              key={`${form.profile_key}-${index}`}
              number={n}
              first={index === 0}
              last={index === draft.length - 1}
              label={sentence.label}
              onEdit={(label) => setDraft(list.edit(draft, index, label))}
              onCommit={() => changeAndCommit(list.leave(draft, index))}
              onMove={(up) =>
                changeAndCommit(list.move(draft, index, up ? index - 1 : index + 1))
              }
              onRemove={() => changeAndCommit(list.remove(draft, index))}
              onAddCondition={addCondition}
              addConditionDisabled={when.length >= MAX_CONDITIONS}
              dryHireAnswer={
                dryHire !== null && sentence.id === dryHire.id
                  ? dryHire.answer
                  : undefined
              }
            >
              {when.length > 0 && (
                <ul className={styles.conditions}>
                  {when.map((condition, j) => {
                    const m = j + 1;
                    const answers = answersFor(condition.field, form.questions);
                    return (
                      <li key={j} className={styles.condition}>
                        <span>{j === 0 ? "Only when" : "and"}</span>
                        <Select
                          variant="quiet"
                          ariaLabel={`What question ${n} waits on, condition ${m}`}
                          value={condition.field}
                          options={withCurrent(sources, condition.field)}
                          onChange={(field) =>
                            changeAndCommit(
                              list.setConditionField(
                                draft,
                                index,
                                j,
                                field,
                                answersFor(field, form.questions)[0]?.value ?? "",
                              ),
                            )
                          }
                        />
                        <span>is</span>
                        <Select
                          variant="quiet"
                          ariaLabel={`Which answer question ${n} waits on, condition ${m}`}
                          value={condition.equals}
                          options={withCurrent(answers, condition.equals)}
                          onChange={(equals) =>
                            changeAndCommit(
                              list.setConditionAnswer(draft, index, j, equals),
                            )
                          }
                        />
                        <IconAction
                          label={`Remove condition ${m} from question ${n}`}
                          disabled={false}
                          onClick={() =>
                            changeAndCommit(list.removeCondition(draft, index, j))
                          }
                        >
                          <TrashIcon className={styles.icon} />
                        </IconAction>
                      </li>
                    );
                  })}
                </ul>
              )}
            </SentenceRow>
          );
        })}
      </ol>

      {error && <InlineBanner variant="error">{error}</InlineBanner>}
    </SettingsGroup>
  );
}

/**
 * A list's own heading inside a form's group: its name, how full it is,
 * and the one way to add to it.
 */
function ListHeader({
  title,
  count,
  action,
}: {
  title: string;
  count: string;
  action: React.ReactNode;
}) {
  return (
    <div className={styles.listHeader}>
      <h4 className={styles.listTitle}>{title}</h4>
      <div className={styles.listMeta}>
        <span className={styles.listCount}>{count}</span>
        {action}
      </div>
    </div>
  );
}


/**
 * One line of the list: text that edits in place — transparent at rest,
 * bordered on hover, black on focus (DESIGN.md's editable-cell
 * treatment) — with its number and its move and remove controls, and
 * the `+` that puts a condition on it. Leaving the line commits it, as
 * every settings field does.
 */
function SentenceRow({
  number,
  first,
  last,
  label,
  onEdit,
  onCommit,
  onMove,
  onRemove,
  onAddCondition,
  addConditionDisabled,
  dryHireAnswer,
  children,
}: {
  number: number;
  first: boolean;
  last: boolean;
  label: string;
  onEdit: (label: string) => void;
  onCommit: () => void;
  onMove: (up: boolean) => void;
  onRemove: () => void;
  onAddCondition: () => void;
  addConditionDisabled: boolean;
  /**
   * The answer that makes an enquiry a dry hire, on the one question the
   * generate step marked. Read-only: the Company never wrote the mark.
   */
  dryHireAnswer?: string;
  /** The question's conditions, under its sentence. */
  children?: React.ReactNode;
}) {
  const n = number;
  return (
    <li className={styles.sentenceItem}>
      <div className={styles.sentenceRow}>
        <span className={styles.sentenceNumber}>{n}</span>
        <input
          aria-label={`Question ${n}`}
          className={styles.sentenceInput}
          value={label}
          onChange={(e) => onEdit(e.target.value)}
          onBlur={onCommit}
          onKeyDown={blurOnEnter}
        />
        <div className={styles.rowActions}>
          <IconAction
            label={`Add a condition to question ${n}`}
            disabled={addConditionDisabled}
            onClick={onAddCondition}
          >
            <PlusIcon className={styles.icon} />
          </IconAction>
          <IconAction
            label={`Move question ${n} up`}
            disabled={first}
            onClick={() => onMove(true)}
          >
            <ChevronUpIcon className={styles.icon} />
          </IconAction>
          <IconAction
            label={`Move question ${n} down`}
            disabled={last}
            onClick={() => onMove(false)}
          >
            <ChevronDownIcon className={styles.icon} />
          </IconAction>
          <IconAction
            label={`Remove question ${n}`}
            disabled={false}
            onClick={onRemove}
          >
            <TrashIcon className={styles.icon} />
          </IconAction>
        </div>
      </div>
      {children}
      {dryHireAnswer !== undefined && (
        <p className={styles.sentenceNote}>{`Dry hire: ${dryHireAnswer}`}</p>
      )}
    </li>
  );
}

/** An icon-only control: its `aria-label` is its name, so no tooltip.
 *  Also the recorded prompt's controls on Settings → Phone. */
export function IconAction({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={styles.iconAction}
    >
      {children}
    </button>
  );
}
